import React from 'react';
import { Modal, Form, Input, Select } from 'antd';
import { workwiseServices, padServices } from '../../../../api';
import { removeLocalDocument } from '../../../../utils/localDocuments';
import PeopleSelect from '../CreatePadDrawer/PeopleSelect';
import '../../../ImportExport/style.css';

const PRIVACY = { PUBLIC: 'public', PRIVATE: 'private' };

/**
 * Sync (upload) a local document to the server.
 *
 * Syncing happens while online and creates a real server document, so it exposes
 * the same fields as the Create Pad drawer (privacy, collaborators, readers) which
 * offline creation could not. Reuses the styled "Export Document" modal look.
 * On API failure the error is shown inline below the name input and the local
 * document is left untouched so the user can retry.
 */
export default function SyncLocalDocumentModal({ open, doc, onClose, onSynced }) {
  const [form] = Form.useForm();
  const [privacy, setPrivacy] = React.useState(PRIVACY.PUBLIC);
  const [collaborators, setCollaborators] = React.useState([]);
  const [readers, setReaders] = React.useState([]);
  const [apiError, setApiError] = React.useState('');
  const [isSyncing, setIsSyncing] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      form.setFieldsValue({ name: doc?.name || '', description: doc?.description || '' });
      setPrivacy(PRIVACY.PUBLIC);
      setCollaborators([]);
      setReaders([]);
      setApiError('');
      setIsSyncing(false);
    }
  }, [open, doc, form]);

  const handleSync = () => {
    form.validateFields().then(async (values) => {
      const trimmed = values.name.trim();
      setIsSyncing(true);
      setApiError('');
      try {
        const formData = new FormData();
        formData.append('name', trimmed);
        formData.append('description', values.description || '');
        formData.append('privacyId', privacy === PRIVACY.PUBLIC ? 1 : 2);
        formData.append('referenceId', '00000000-0000-0000-0000-000000000000');
        formData.append('referenceType', 1);
        formData.append('directoryId', '00000000-0000-0000-0000-000000000000');
        formData.append('documentType', 6);
        formData.append('attachments[0].name', trimmed);

        let memberIndex = 0;
        if (privacy === PRIVACY.PRIVATE) {
          readers.forEach((memberId) => {
            formData.append(`members[${memberIndex}].memberId`, memberId);
            formData.append(`members[${memberIndex}].memberRightType`, 1);
            memberIndex++;
          });
        }
        collaborators.forEach((memberId) => {
          formData.append(`members[${memberIndex}].memberId`, memberId);
          formData.append(`members[${memberIndex}].memberRightType`, 2);
          memberIndex++;
        });

        const response = await workwiseServices.addDocument(formData);
        const created = response?.data?.data?.[0];
        const attachmentId = created?.attachments?.[0]?.attachmentId;

        if (!attachmentId) {
          throw new Error('Server did not return a document id.');
        }

        // Transfer the locally-edited content into the new server document.
        await padServices.saveToDatabase(
          attachmentId,
          doc?.content?.milepadData || '',
          doc?.content?.summary || ''
        );

        removeLocalDocument(doc.id);
        onSynced?.(attachmentId);
        onClose?.();
      } catch (e) {
        const apiMessage =
          e?.response?.data?.message || e?.message || 'Failed to sync document. Please try again.';
        setApiError(apiMessage);
      } finally {
        setIsSyncing(false);
      }
    });
  };

  return (
    <Modal
      title="Sync Document"
      visible={open}
      onOk={handleSync}
      onCancel={isSyncing ? undefined : onClose}
      afterClose={() => form.resetFields()}
      okText="Sync"
      cancelText="Cancel"
      confirmLoading={isSyncing}
      okButtonProps={{ disabled: isSyncing, className: 'export-modal-btn-download' }}
      cancelButtonProps={{ className: 'export-modal-btn-cancel' }}
      maskClosable={!isSyncing}
      className="export-document-modal"
      bodyStyle={{ paddingTop: 10 }}
      centered
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Document Name"
          name="name"
          style={{ marginBottom: 14 }}
          // API errors surface inline under the input, in the same style as
          // validation errors.
          validateStatus={apiError ? 'error' : undefined}
          help={apiError || undefined}
          rules={[{ required: true, message: 'Please enter a document name.' }]}
        >
          <Input
            placeholder="Enter document name"
            disabled={isSyncing}
            className="export-modal-input"
            onChange={() => { if (apiError) setApiError(''); }}
            onPressEnter={handleSync}
          />
        </Form.Item>

        <Form.Item label="Privacy" style={{ marginBottom: 14 }}>
          <Select
            value={privacy}
            onChange={setPrivacy}
            disabled={isSyncing}
            options={[
              { value: PRIVACY.PUBLIC, label: 'Public' },
              { value: PRIVACY.PRIVATE, label: 'Private' },
            ]}
          />
        </Form.Item>

        <Form.Item label="Collaborators" style={{ marginBottom: 14 }}>
          <PeopleSelect
            value={collaborators}
            onChange={setCollaborators}
            placeholder="Select Collaborators"
            disabled={isSyncing}
          />
        </Form.Item>

        {privacy === PRIVACY.PRIVATE && (
          <Form.Item label="Readers" style={{ marginBottom: 14 }}>
            <PeopleSelect
              value={readers}
              onChange={setReaders}
              placeholder="Select Readers"
              disabled={isSyncing}
            />
          </Form.Item>
        )}

        <Form.Item label="Description" name="description" style={{ marginBottom: 14 }}>
          <Input.TextArea
            placeholder="Enter description"
            disabled={isSyncing}
            rows={3}
            maxLength={500}
          />
        </Form.Item>

        <div className="export-format-info">
          <div className="export-format-label">
            This will upload the document to the server and remove it from local documents.
          </div>
        </div>
      </Form>
    </Modal>
  );
}
